/**
 * HOW FAR A 1:1 CALL GOT, per session: did the partner arrive, and did the
 * connection come up.
 *
 * WHY THIS EXISTS (David, 2026-10-27). A call that never connected (the
 * person declined, or never answered) used to end exactly like one that did:
 * the flow's `handleEndCall` put up "How was your session?" for a frame or
 * two, and then the seat's next poll took the flow off the page. A rating
 * form for a conversation that never happened is wrong even for one frame,
 * so the ending now asks this record first (`callEndRoute`), synchronously,
 * before anything is rendered: the seat's poll, the server's `end` and the
 * caller's own Cancel can each be what ends the call, and none of them may
 * have to wait for a round trip to know which screen comes next.
 *
 * WHAT COUNTS AS CONNECTED. The RTCPeerConnection reaching `connected` for
 * this session, which only the device can see. The server has no marker for
 * it: it knows the requester REACHED the call (SeekerNoShowJob::seen, on
 * their Join or first signal), and a call can be reached and still never
 * connect. The two agree where they overlap, by construction: a connection
 * needs the requester's answer, the answer is a signal, and a signal marks
 * them reached, so a call the server voided as `seeker_no_show` can never be
 * recorded here as connected.
 *
 * WHAT COUNTS AS ARRIVED. The partner's offer or answer reaching this screen:
 * R12's "has the partner arrived" (PeerAudioRoom), the same signal the
 * server marks the requester reached by.
 *
 * ONE WRITER EACH (class E): `noteCallConnected` from the two connection-state
 * handlers (the stage's and the detached one in `liveCall.ts`, class D),
 * `notePartnerArrived` from the stage's offer/answer. "Connected" is also
 * kept in sessionStorage, so a reload in the middle of a real call still ends
 * into its feedback.
 */

export interface CallProgress {
  /** An offer or answer from the partner reached this device. */
  arrived: boolean;
  /** The connection reached `connected` at least once. */
  connected: boolean;
}

const STORAGE_KEY = 'ee:call-connected';
/** Enough for every call a tab could hold; the oldest go first. */
const STORAGE_CAP = 20;

const records = new Map<number, CallProgress>();

function recordFor(sessionId: number): CallProgress {
  let record = records.get(sessionId);
  if (!record) {
    record = { arrived: false, connected: false };
    records.set(sessionId, record);
  }
  return record;
}

function storedConnected(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

export function notePartnerArrived(sessionId: number): void {
  recordFor(sessionId).arrived = true;
}

export function noteCallConnected(sessionId: number): void {
  const record = recordFor(sessionId);
  if (record.connected) return;
  record.connected = true;
  try {
    const ids = storedConnected().filter((id) => id !== sessionId);
    ids.push(sessionId);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-STORAGE_CAP)));
  } catch {
    /* storage unavailable: the in-memory record still holds */
  }
}

export function callProgress(sessionId: number): CallProgress {
  const record = recordFor(sessionId);
  if (!record.connected && storedConnected().includes(sessionId)) record.connected = true;
  return { ...record };
}

/** Test seam: forget every record, here and in storage. */
export function resetCallProgress(): void {
  records.clear();
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * WHERE A FINISHED CALL GOES — asked by both flows' `handleEndCall`, before
 * anything is rendered.
 *
 *  - `feedback`: the call connected. The rating screen, as always.
 *  - `unanswered`: the CALLER's call the other person did not take — they
 *    said Not now, or the answer window ran out — and the caller did not
 *    cancel it themselves. The caller gets "{Name} can't talk right now".
 *  - `quiet`: anything else that never connected (the caller cancelled, the
 *    requester's side of a call that never came up, a connection that failed
 *    after they arrived). No rating screen, no modal: the flow's ordinary
 *    after-call clean-up runs straight away.
 */
export type CallEndRoute = 'feedback' | 'unanswered' | 'quiet';

export function callEndRoute({
  role,
  progress,
  selfEnded,
}: {
  role: 'giver' | 'seeker';
  progress: CallProgress;
  /** This person pressed End / Cancel (the stage or the pill). */
  selfEnded: boolean;
}): CallEndRoute {
  if (progress.connected) return 'feedback';
  if (role !== 'giver' || selfEnded) return 'quiet';
  return progress.arrived ? 'quiet' : 'unanswered';
}
